import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Users, Search, Edit2, CheckCircle2, User, FolderKanban, FileText, Save, X, Building2, UserCheck } from 'lucide-react';

export const AdminClientsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingClient, setEditingClient] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE' | 'PENDING'>('ACTIVE');

  const queryClient = useQueryClient();

  const { data: clientsRes, isLoading } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => api.get('/admin/clients').then((res) => res.data.data),
  });

  const updateMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.put(`/admin/clients/${id}`, { notes, status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      setEditingClient(null);
    },
  });

  const clients = clientsRes || [];
  const filteredClients = clients.filter((c: any) => {
    const q = searchTerm.toLowerCase();
    return (
      c.user?.firstName?.toLowerCase().includes(q) ||
      c.user?.lastName?.toLowerCase().includes(q) ||
      c.user?.email?.toLowerCase().includes(q) ||
      c.companyName?.toLowerCase().includes(q)
    );
  });

  const startEdit = (c: any) => {
    setEditingClient(c);
    setNotes(c.notes || '');
    setStatus(c.status);
  };

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              AUTHOR & CLIENT CRM
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <Users className="w-7 h-7 text-[#FF6B4A]" />
            <span>Client &amp; Author Directory</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Manage contracted publishing clients, author accounts, internal notes, and staff assignments.
          </p>
        </div>

        <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono font-bold text-slate-700 shrink-0">
          <UserCheck className="w-4 h-4 text-brand-600" />
          <span>Active Authors: <strong className="text-slate-900">{clients.length}</strong></span>
        </div>
      </div>

      {/* 2. Search & Filter Bar */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search authors, imprints, or emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 placeholder-slate-400 focus:outline-hidden focus:bg-white focus:border-brand-500 transition-colors"
          />
        </div>
        <span className="text-xs font-mono text-slate-500">
          Showing <strong>{filteredClients.length}</strong> of {clients.length} accounts
        </span>
      </div>

      {/* 3. Table */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">
          Loading client directory...
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-slate-200 text-center space-y-3 shadow-sm">
          <Users className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-900">No Clients Found</h3>
          <p className="text-xs text-slate-500">No registered clients matched your search.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200/90 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-50/80 text-slate-500 font-mono text-[11px] uppercase border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 font-bold">Client / Author</th>
                  <th className="px-6 py-4 font-bold">Organization / Imprint</th>
                  <th className="px-6 py-4 font-bold">Assigned Staff</th>
                  <th className="px-6 py-4 font-bold">Projects</th>
                  <th className="px-6 py-4 font-bold">Status</th>
                  <th className="px-6 py-4 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-sans">
                {filteredClients.map((c: any) => (
                  <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-900">{c.user?.firstName} {c.user?.lastName}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{c.user?.email}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-700 font-medium">
                      {c.companyName || 'Independent Author'}
                    </td>
                    <td className="px-6 py-4 font-mono text-[11px] text-slate-600">
                      {c.assignedStaff ? `${c.assignedStaff.firstName} ${c.assignedStaff.lastName}` : <span className="text-slate-400 italic">Unassigned</span>}
                    </td>
                    <td className="px-6 py-4 font-mono font-bold text-brand-600">
                      {c.projectsCount || 0} Projects
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold uppercase ${
                        c.status === 'ACTIVE' 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => startEdit(c)}
                        className="px-3.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                      >
                        <Edit2 className="w-3.5 h-3.5 text-[#FF6B4A]" />
                        <span>Edit / Notes</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {editingClient && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-brand-700 font-bold">CLIENT PROFILE</span>
                <h3 className="font-serif text-xl font-bold text-slate-900">
                  {editingClient.user?.firstName} {editingClient.user?.lastName}
                </h3>
              </div>
              <button 
                onClick={() => setEditingClient(null)} 
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Client Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Private Internal Staff Notes
                </label>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes visible only to FOR THE TRUTH publishing staff..."
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-hidden focus:border-brand-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => setEditingClient(null)}
                className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-bold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => updateMutation.mutate(editingClient.id)}
                disabled={updateMutation.isPending}
                className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md transition-colors cursor-pointer disabled:opacity-50"
              >
                Save Client Notes
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
